import { useCallback, useRef, useState } from 'react'

import { GAME_TYPE } from 'constants/enums/GameTypeEnum'

import { postIdea } from 'services/ideas.service'

import {
	fetchCompanyExamples,
	fetchCompetitorResponse,
	fetchDisruptionPrompt,
} from '../api'
import {
	Conversation,
	DisruptionScenario,
	DisruptionType,
	GameSession,
	ResponseIndex,
	createEmptyScenario,
	initialPromptConversation,
} from '../types'
import { buildIdeationPayload, updateAIResponse, userMessage } from '../utils'

// ---------------------------------------------------------------------------
// useScenario
// Owns one disruption type's scenario state independently.
// No activeType tracking — this hook simply IS one type.
// To add a new disruption type, instantiate another useScenario.
// ---------------------------------------------------------------------------
export function useScenario(type: DisruptionType, session: GameSession) {
	// Seed from session.initialScenarios[type] once the API provides it.
	const [scenario, setScenario] =
		useState<DisruptionScenario>(createEmptyScenario)
	const [promptConversation, setPromptConversation] = useState<
		Conversation[]
	>(() => initialPromptConversation[type])
	const [disruptionIsLoading, setDisruptionIsLoading] = useState(false)
	const [examplesIsLoading, setExamplesIsLoading] = useState(false)
	const [competitorIsLoading, setCompetitorIsLoading] = useState(false)

	// Keep a ref so empty-dep callbacks always read current values.
	const sessionRef = useRef(session)
	sessionRef.current = session
	const scenarioRef = useRef(scenario)
	scenarioRef.current = scenario
	const promptConversationRef = useRef(promptConversation)
	promptConversationRef.current = promptConversation

	const updateSlot = useCallback(
		(idx: ResponseIndex, responses: string[]) => {
			const msg = JSON.stringify({ responses })
			setScenario((prev) => {
				const newResponses = [...prev.responses]
				newResponses[idx] = responses
				const newConvo = [...prev.responseConversation]
				newConvo[idx] = [
					...prev.responseConversation[idx],
					userMessage,
					updateAIResponse(msg),
				]
				return {
					...prev,
					responses: newResponses,
					responseConversation: newConvo,
				}
			})
		},
		[]
	)

	const generateDisruptionScenario = useCallback(async () => {
		const { companyName, companyProblem, topic } = sessionRef.current
		setDisruptionIsLoading(true)
		try {
			return await fetchDisruptionPrompt(type, {
				companyName,
				companyProblem,
				conversation: promptConversationRef.current,
				topic,
			})
		} finally {
			setDisruptionIsLoading(false)
		}
	}, [type])

	const generateCompanyExamples = useCallback(async (promptText: string) => {
		const { companyName, topic } = sessionRef.current
		setExamplesIsLoading(true)
		try {
			return await fetchCompanyExamples({
				companyName,
				promptText,
				topic,
			})
		} finally {
			setExamplesIsLoading(false)
		}
	}, [])

	const initializeScenario = useCallback(async () => {
		const result = await generateDisruptionScenario()
		if (!result) return
		const { promptText } = result

		setPromptConversation((prev) => [
			...prev,
			userMessage,
			updateAIResponse(promptText),
		])
		setScenario({
			...createEmptyScenario(),
			prompt: promptText,
		})

		const examples = await generateCompanyExamples(promptText)
		if (examples) {
			updateSlot(ResponseIndex.CompanyExamples, examples)
		}
	}, [generateDisruptionScenario, generateCompanyExamples, updateSlot])

	const startNewRound = useCallback(async () => {
		setScenario(createEmptyScenario())
		await initializeScenario()
	}, [initializeScenario])

	// Called when the tab for this type becomes active and has no prompt yet.
	const initializeIfEmpty = useCallback(async () => {
		if (!scenarioRef.current.prompt) {
			await initializeScenario()
		}
	}, [initializeScenario])

	const loadExamplesForPrompt = useCallback(
		async (promptText: string) => {
			setScenario({ ...createEmptyScenario(), prompt: promptText })
			const examples = await generateCompanyExamples(promptText)
			if (examples) {
				updateSlot(ResponseIndex.CompanyExamples, examples)
			}
		},
		[generateCompanyExamples, updateSlot]
	)

	const generateCompetitorResponse = useCallback(async () => {
		const current = scenarioRef.current
		if (!current.prompt) return
		const { companyName } = sessionRef.current
		const idx = ResponseIndex.CompetitorResponse
		setCompetitorIsLoading(true)
		try {
			return await fetchCompetitorResponse({
				companyName,
				conversation: [
					...current.responseConversation[idx],
					userMessage,
				],
				problem: current.prompt,
				userInput1: current.authorIdeation[0],
			})
		} finally {
			setCompetitorIsLoading(false)
		}
	}, [])

	const updateCompetitorResponse = useCallback(async () => {
		const responses = await generateCompetitorResponse()
		if (responses) {
			updateSlot(ResponseIndex.CompetitorResponse, responses)
		}
	}, [generateCompetitorResponse, updateSlot])

	const setCompetitorResponse = useCallback((editedText: string) => {
		setScenario((prev) => {
			const idx = ResponseIndex.CompetitorResponse
			const newResponses = [...prev.responses]
			newResponses[idx] = [editedText]
			const newConvo = [...prev.responseConversation]
			const convo = [...newConvo[idx]]
			const lastIdx = convo.length - 1
			const editedEntry = updateAIResponse(
				JSON.stringify({ responses: [editedText] })
			)
			if (lastIdx >= 0 && convo[lastIdx].role === 'assistant') {
				convo[lastIdx] = editedEntry
			} else {
				convo.push(editedEntry)
			}
			newConvo[idx] = convo
			return {
				...prev,
				responses: newResponses,
				responseConversation: newConvo,
			}
		})
	}, [])

	const setAuthorIdeation = useCallback((i: number, val: string) => {
		setScenario((prev) => ({
			...prev,
			authorIdeation: prev.authorIdeation.map((cur, idx) =>
				i === idx ? val : cur
			),
		}))
	}, [])

	const submitIdeas = useCallback(async () => {
		const { companyName, companyProblem, gameId, topic } =
			sessionRef.current
		const { authorIdeation, prompt, responses } = scenarioRef.current
		const competitorResponse =
			responses[ResponseIndex.CompetitorResponse]?.join('\n') ?? ''

		const returnIdeation = buildIdeationPayload(
			prompt,
			authorIdeation,
			competitorResponse
		)

		const response = await postIdea({
			gameId: gameId ?? undefined,
			gameTypeId: GAME_TYPE.BATTLE_SHIPS.value,
			innovationTopic: topic,
			innovationCompany: companyName,
			innovationTopicManual: companyProblem,
			authorIdeation: returnIdeation,
		})
		if (response.status !== 200) {
			throw new Error(`Failed to submit idea. Status: ${response.status}`)
		}
	}, [])

	return {
		scenario,
		disruptionIsLoading,
		examplesIsLoading,
		competitorIsLoading,
		startNewRound,
		initializeIfEmpty,
		loadExamplesForPrompt,
		updateCompetitorResponse,
		setCompetitorResponse,
		setAuthorIdeation,
		submitIdeas,
	}
}
